import React, { useState } from 'react';
import PropTypes from 'prop-types';
import pluralize from 'pluralize';
import { formatWithCommas } from 'helpers/formatters/number_formats';
import ProductFeature from './product_feature';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRectangleHistoryCirclePlus, faTableLayout } from '@fortawesome/pro-solid-svg-icons';
import AddToList from '../../properties/add_to_list';
import { connect } from 'react-redux';
import { PROPERTY_MODEL } from '../../../constants';
import OffMarketMapTableViewToggle from './off_market_map_table_view_toggle';
import { useMediaQuery } from 'react-responsive';
import { Button, FormCheck, OverlayTrigger, Tooltip } from 'react-bootstrap-5';
import { setSearchTableColumnKeys, setUpdateResultsOnMapMove } from '../store/actions/ui';
import { updateSearchTableColumns } from '../../../helpers/prospect_api';
import ManageColumnsModal from './manage_columns_modal';
import RethinkMode from '../../rethink_mode/rethink_mode';


function OffMarketResultsHeader(
  {
    allResults,
    criteria,
    selectAll,
    selectedPropertyIds,
    onToggleAllSelected,
    searchTableFields,
    setSearchTableColumnKeys,
    setUpdateResultsOnMapMove,
    showTableView,
    total,
    updateResultsOnMapMove
  }
) {
  const showFullSizeButton = useMediaQuery({ minWidth: 1193 });
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleModalCancel = () => {
    setShowModal(false);
  };

  const handleModalSave = (newSelectedFields) => {
    setSaving(true);
    const searchTableKeys = newSelectedFields.map(field => field.value);
    updateSearchTableColumns({ connect_search_table_view_columns: searchTableKeys })
      .then(() => {
        setShowModal(false);
        setSearchTableColumnKeys(searchTableKeys);
      })
      .finally(() => setSaving(false));
  };

  return (
    <div
      className="p-3 d-flex align-items-center justify-content-between top-0 position-relative z-3 bg-white shadow-md flex-grow-0"
    >
      <div className="fw-semibold">{formatWithCommas(total)} {pluralize('property', total)}</div>
      <div className="d-flex gap-4 align-items-center">
        <ProductFeature product="prospecting">
          <div className="d-flex gap-4 align-items-center">
            {showTableView && (
              <>
                <div className="d-flex align-items-center gap-2">
                  <div>Update results when map moves</div>
                  <FormCheck
                    checked={updateResultsOnMapMove}
                    onChange={() => setUpdateResultsOnMapMove(!updateResultsOnMapMove)}
                  />
                </div>
                {showFullSizeButton && (
                  <div className="d-flex gap-4 align-items-center">
                    <Button variant="text-primary" onClick={() => setShowModal(!showModal)}>
                      <FontAwesomeIcon icon={faTableLayout} />
                      Manage Columns
                    </Button>
                    {(selectedPropertyIds.size > 0 || selectAll) && (
                      <AddToList
                        criteria={criteria}
                        itemCount={selectAll ? total : selectedPropertyIds.size}
                        itemIds={selectedPropertyIds}
                        itemType={PROPERTY_MODEL}
                        selectAll={selectAll}
                      >
                        <Button variant="text-primary">
                          <FontAwesomeIcon icon={faRectangleHistoryCirclePlus} />
                          Add to List
                        </Button>
                      </AddToList>
                    )}
                    {selectedPropertyIds.size === 0 && !selectAll && (
                      <Button disabled variant="text-primary">
                        <FontAwesomeIcon icon={faRectangleHistoryCirclePlus} />
                        Add to List
                      </Button>
                    )}
                  </div>
                )}
                {!showFullSizeButton && (
                  <div className="d-flex gap-4 align-items-center">
                    <OverlayTrigger overlay={<Tooltip>Manage Columns</Tooltip>} placement="top">
                      <FontAwesomeIcon
                        className="p-2 text-primary clickable"
                        icon={faTableLayout}
                        onClick={() => setShowModal(!showModal)}
                      />
                    </OverlayTrigger>
                    {(selectedPropertyIds.size > 0 || selectAll) && (
                      <AddToList
                        criteria={criteria}
                        itemCount={selectAll ? total : selectedPropertyIds.size}
                        itemIds={selectedPropertyIds}
                        itemType={PROPERTY_MODEL}
                        selectAll={selectAll}
                      >
                        <FontAwesomeIcon className="p-2 text-primary clickable" icon={faRectangleHistoryCirclePlus} />
                      </AddToList>
                    )}
                    {selectedPropertyIds.size === 0 && !selectAll && (
                      <FontAwesomeIcon className="p-2 text-muted" icon={faRectangleHistoryCirclePlus} />
                    )}
                  </div>
                )}
                <ManageColumnsModal
                  initialSelectedColumns={searchTableFields}
                  saving={saving}
                  show={showModal}
                  onHide={handleModalCancel}
                  onSave={handleModalSave}
                />
              </>
            )}
            {!showTableView && (
              <>
                <RethinkMode>
                  <label>
                    <div className="d-flex gap-2">
                      <input
                        checked={selectAll}
                        data-pendo-tag="results_select_all_checkbox"
                        disabled={allResults.length === 0}
                        type="checkbox"
                        onChange={onToggleAllSelected}
                      />
                      <div>Select All</div>
                    </div>
                  </label>
                </RethinkMode>
                {(selectedPropertyIds.size > 0 || selectAll) && (
                  <AddToList
                    criteria={criteria}
                    itemCount={selectAll ? total : selectedPropertyIds.size}
                    itemIds={selectedPropertyIds}
                    itemType={PROPERTY_MODEL}
                    selectAll={selectAll}
                  >
                    <FontAwesomeIcon className="text-primary clickable" icon={faRectangleHistoryCirclePlus} />
                  </AddToList>
                )}
                {selectedPropertyIds.size === 0 && !selectAll && (
                  <FontAwesomeIcon className="text-muted" icon={faRectangleHistoryCirclePlus} />
                )}
              </>
            )}
          </div>
        </ProductFeature>
        <OffMarketMapTableViewToggle />
      </div>
    </div>
  );
}

OffMarketResultsHeader.propTypes = {
  allResults: PropTypes.arrayOf(PropTypes.shape({
    address: PropTypes.string.isRequired,
    city: PropTypes.string.isRequired,
    state: PropTypes.string.isRequired
  })),
  criteria: PropTypes.object,
  searchTableFields: PropTypes.array,
  selectAll: PropTypes.bool.isRequired,
  selectedPropertyIds: PropTypes.object.isRequired,
  setSearchTableColumnKeys: PropTypes.func.isRequired,
  setUpdateResultsOnMapMove: PropTypes.func.isRequired,
  showTableView: PropTypes.bool.isRequired,
  total: PropTypes.number,
  updateResultsOnMapMove: PropTypes.bool.isRequired,
  onToggleAllSelected: PropTypes.func.isRequired
};

const mapStateToProps = ({
  criteria,
  results: {
    offMarketResults,
    totalOffMarketResults
  },
  ui: { showTableView, updateResultsOnMapMove }
}) => {
  return {
    allResults: offMarketResults,
    criteria,
    setSearchTableColumnKeys,
    setUpdateResultsOnMapMove,
    showTableView,
    total: totalOffMarketResults,
    updateResultsOnMapMove
  };
};
export default connect(
  mapStateToProps,
  { setSearchTableColumnKeys, setUpdateResultsOnMapMove }
)(OffMarketResultsHeader);
